/**
 * Roulette-specific event types and interfaces
 * Uses same event type names as blackjack (unified contract)
 * with roulette-specific data structures
 */

export namespace RouletteEvents {
  /**
   * Event types for roulette game
   * Same names as blackjack for unified suspension logic
   */
  export enum Type {
    WELCOME = 'welcome',
    PLACE_BET = 'place_bet',
    BETS_UPDATE = 'bets_update',
    BET_CONFIRMATION = 'bet_confirmation',
    GAME_OVER = 'game_over',
    BET_TIMER_EXPIRED = 'bet_timer_expired',
  }

  /**
   * Roulette bet types.
   * Comments encode NET profit odds; the wire `payout` is GROSS
   * (gross multiplier = net + 1), so a mapper author sends the gross return.
   */
  export type BetType =
    | 'straight' // Single number — net 35:1 (gross 36×)
    | 'split' // Two adjacent numbers — net 17:1 (gross 18×)
    | 'street' // Three numbers in a row — net 11:1 (gross 12×)
    | 'corner' // Four numbers — net 8:1 (gross 9×)
    | 'line' // Six numbers — net 5:1 (gross 6×)
    | 'column' // Column of 12 — net 2:1 (gross 3×)
    | 'dozen' // First/Second/Third 12 — net 2:1 (gross 3×)
    | 'red' // Red numbers — net 1:1 (gross 2×)
    | 'black' // Black numbers — net 1:1 (gross 2×)
    | 'odd' // Odd numbers — net 1:1 (gross 2×)
    | 'even' // Even numbers — net 1:1 (gross 2×)
    | 'low' // 1-18 — net 1:1 (gross 2×)
    | 'high'; // 19-36 — net 1:1 (gross 2×)

  /**
   * Roulette wheel colors
   */
  export type Color = 'red' | 'black' | 'green';

  /**
   * A single bet placed by the player
   */
  export interface Bet {
    type: BetType;
    numbers: number[]; // Numbers covered by this bet, each 0-36 (European single-zero)
    amount: number;
    betId?: string; // Optional individual bet ID
  }

  /**
   * Bet with outcome information (used in game_over)
   */
  export interface BetOutcome extends Bet {
    won: boolean;
    payout: number; // Amount won (0 if lost)
  }

  /**
   * Base interface for all roulette events
   */
  interface BaseEvent {
    type: Type;
  }

  /**
   * Welcome event when player joins
   */
  export interface Welcome extends BaseEvent {
    type: Type.WELCOME;
    data?: {
      message?: string;
    };
  }

  /**
   * Place bet prompt event
   */
  export interface PlaceBet extends BaseEvent {
    type: Type.PLACE_BET;
    data?: {
      minBet?: number;
      maxBet?: number;
    };
  }

  /**
   * Silent live-table snapshot during the betting window. Full snapshot,
   * last write wins — never a diff. Empty `bets` is a valid state (player
   * cleared the table). No roundId: the round does not exist yet. Carries
   * no money meaning — bet_confirmation stays the source of truth.
   */
  export interface BetsUpdate extends BaseEvent {
    type: Type.BETS_UPDATE;
    data: {
      bets: Bet[];
      totalBet: number;
    };
  }

  /**
   * Bet confirmation event - all bets placed for the round
   */
  export interface BetConfirmation extends BaseEvent {
    type: Type.BET_CONFIRMATION;
    data: {
      bets: Bet[];
      totalBet: number;
      roundId: string; // Round correlation ID
    };
  }

  /**
   * Game over event - spin result with all bet outcomes
   */
  export interface GameOver extends BaseEvent {
    type: Type.GAME_OVER;
    data: {
      spinNumber: number; // Winning number, 0-36 (European single-zero)
      spinColor: Color;
      bets: BetOutcome[];
      totalBet: number;
      totalWin: number; // Total payout
      roundId: string;
    };
  }

  /**
   * Bet timer expired event - triggers AI suspension
   */
  export interface BetTimerExpired extends BaseEvent {
    type: Type.BET_TIMER_EXPIRED;
    data?: undefined;
  }

  /**
   * Union type of all roulette events
   */
  export type Event = Welcome | PlaceBet | BetsUpdate | BetConfirmation | GameOver | BetTimerExpired;

  /**
   * Helper functions to create roulette events
   */
  export function create<T extends Event>(type: T['type'], data?: T['data']): T {
    return {
      type,
      data,
    } as T;
  }

  export function createWelcome(message?: string): Welcome {
    return create(Type.WELCOME, message ? { message } : undefined);
  }

  export function createPlaceBet(minBet?: number, maxBet?: number): PlaceBet {
    return create(Type.PLACE_BET, minBet || maxBet ? { minBet, maxBet } : undefined);
  }

  export function createBetsUpdate(bets: Bet[], totalBet: number): BetsUpdate {
    return create(Type.BETS_UPDATE, { bets, totalBet });
  }

  export function createBetConfirmation(bets: Bet[], totalBet: number, roundId: string): BetConfirmation {
    return create(Type.BET_CONFIRMATION, { bets, totalBet, roundId });
  }

  export function createGameOver(
    spinNumber: number,
    spinColor: Color,
    bets: BetOutcome[],
    totalBet: number,
    totalWin: number,
    roundId: string
  ): GameOver {
    return create(Type.GAME_OVER, {
      spinNumber,
      spinColor,
      bets,
      totalBet,
      totalWin,
      roundId,
    });
  }

  export function createBetTimerExpired(): BetTimerExpired {
    return create(Type.BET_TIMER_EXPIRED, undefined);
  }

  /**
   * Type guards
   */
  export function isWelcome(event: Event): event is Welcome {
    return event.type === Type.WELCOME;
  }

  export function isPlaceBet(event: Event): event is PlaceBet {
    return event.type === Type.PLACE_BET;
  }

  export function isBetsUpdate(event: Event): event is BetsUpdate {
    return event.type === Type.BETS_UPDATE;
  }

  export function isBetConfirmation(event: Event): event is BetConfirmation {
    return event.type === Type.BET_CONFIRMATION;
  }

  export function isGameOver(event: Event): event is GameOver {
    return event.type === Type.GAME_OVER;
  }

  export function isBetTimerExpired(event: Event): event is BetTimerExpired {
    return event.type === Type.BET_TIMER_EXPIRED;
  }

  /** Canonical runtime-enumerable values used by validators. */
  export const BET_TYPES: readonly BetType[] = [
    'straight',
    'split',
    'street',
    'corner',
    'line',
    'column',
    'dozen',
    'red',
    'black',
    'odd',
    'even',
    'low',
    'high',
  ] as const;
  export const COLORS: readonly Color[] = ['red', 'black', 'green'] as const;
}
